import { Card, Col, Row, Statistic, Typography, Spin } from "antd";
import {
  FileTextOutlined,
  CalendarOutlined,
  UserOutlined,
  QuestionCircleOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useGetDashboardStatistics } from "@/hooks/dashboard/useGetDashboardStatistics";
import { useMemo } from "react";
import { useExamStore, ThemeMode } from "@store";
import { useThemeDetector } from "@hooks";

// Lazy load ApexCharts to avoid SSR issues
import { lazy, Suspense } from "react";
const Chart = lazy(() => import("react-apexcharts"));

export const DashboardPage = () => {
  const { t } = useTranslation();
  const { data: statistics, isLoading } = useGetDashboardStatistics();
  const theme = useExamStore((state) => state.theme);
  const { isDarkTheme } = useThemeDetector();
  
  const isDark = theme === ThemeMode.Dark || (theme === ThemeMode.System && isDarkTheme);

  // Memoized chart data
  const barChartData = useMemo(() => {
    const categories = statistics?.examTermsByMonth?.map(d => d.month) || [];
    const data = statistics?.examTermsByMonth?.map(d => d.count) || [];
    
    return {
      options: {
        chart: {
          id: 'exam-terms-chart',
          background: 'transparent',
          foreColor: isDark ? '#fff' : '#333',
          toolbar: { show: false },
        },
        theme: { mode: isDark ? 'dark' as const : 'light' as const },
        plotOptions: {
          bar: { borderRadius: 4, columnWidth: '60%' },
        },
        dataLabels: { enabled: false },
        xaxis: {
          categories,
          labels: { style: { colors: isDark ? '#fff' : '#333' } },
        },
        yaxis: {
          labels: { style: { colors: isDark ? '#fff' : '#333' } },
        },
        colors: ['#F1B80E'],
        grid: { borderColor: isDark ? '#333' : '#e0e0e0' },
      },
      series: [{ name: t("pages.dashboard.examTerms"), data }],
    };
  }, [statistics?.examTermsByMonth, isDark, t]);

  const donutChartData = useMemo(() => {
    const labels = statistics?.questionsByType?.map(d => d.type) || [];
    const series = statistics?.questionsByType?.map(d => d.count) || [];
    
    return {
      options: {
        chart: {
          id: 'questions-type-chart',
          background: 'transparent',
          foreColor: isDark ? '#fff' : '#333',
        },
        theme: { mode: isDark ? 'dark' as const : 'light' as const },
        labels,
        colors: ['#F1B80E', '#3498db', '#2ecc71', '#e74c3c', '#9b59b6'],
        legend: {
          position: 'bottom' as const,
          labels: { colors: isDark ? '#fff' : '#333' },
        },
        dataLabels: { enabled: true },
        plotOptions: {
          pie: {
            donut: {
              size: '65%',
              labels: {
                show: true,
                total: {
                  show: true,
                  label: t("pages.dashboard.total"),
                  color: isDark ? '#fff' : '#333',
                },
              },
            },
          },
        },
      },
      series,
    };
  }, [statistics?.questionsByType, isDark, t]);

  const horizontalBarData = useMemo(() => {
    const categories = statistics?.questionsByCategory?.map(d => d.category) || [];
    const data = statistics?.questionsByCategory?.map(d => d.count) || [];
    
    return {
      options: {
        chart: {
          id: 'questions-category-chart',
          background: 'transparent',
          foreColor: isDark ? '#fff' : '#333',
          toolbar: { show: false },
        },
        theme: { mode: isDark ? 'dark' as const : 'light' as const },
        plotOptions: {
          bar: { horizontal: true, borderRadius: 4, barHeight: '70%' },
        },
        dataLabels: { enabled: false },
        xaxis: {
          categories,
          labels: { style: { colors: isDark ? '#fff' : '#333' } },
        },
        yaxis: {
          labels: { style: { colors: isDark ? '#fff' : '#333' } },
        },
        colors: ['#3498db'],
        grid: { borderColor: isDark ? '#333' : '#e0e0e0' },
      },
      series: [{ name: t("pages.dashboard.questions"), data }],
    };
  }, [statistics?.questionsByCategory, isDark, t]);

  const ChartLoader = () => (
    <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Spin />
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 1 }}
    >
      <Typography.Title level={2} style={{ marginBottom: 24 }}>
        {t("pages.dashboard.title")}
      </Typography.Title>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={8} xl={4}>
          <Card>
            <Statistic
              title={t("pages.dashboard.totalExams")}
              value={statistics?.totalExams ?? 0}
              prefix={<FileTextOutlined />}
              loading={isLoading}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={8} xl={5}>
          <Card>
            <Statistic
              title={t("pages.dashboard.totalExamTerms")}
              value={statistics?.totalExamTerms ?? 0}
              prefix={<CalendarOutlined />}
              loading={isLoading}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={8} xl={5}>
          <Card>
            <Statistic
              title={t("pages.dashboard.activeExamTerms")}
              value={statistics?.activeExamTerms ?? 0}
              prefix={<CheckCircleOutlined />}
              loading={isLoading}
              valueStyle={{ color: "#3f8600" }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={8} xl={5}>
          <Card>
            <Statistic
              title={t("pages.dashboard.totalParticipants")}
              value={statistics?.totalParticipants ?? 0}
              prefix={<UserOutlined />}
              loading={isLoading}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={8} xl={5}>
          <Card>
            <Statistic
              title={t("pages.dashboard.totalQuestions")}
              value={statistics?.totalQuestions ?? 0}
              prefix={<QuestionCircleOutlined />}
              loading={isLoading}
            />
          </Card>
        </Col>
      </Row>

      {/* Charts Row */}
      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        {/* Exam Terms by Month - Bar Chart */}
        <Col xs={24} lg={14}>
          <Card title={t("pages.dashboard.examTermsByMonth")} loading={isLoading}>
            {!isLoading && (
              <Suspense fallback={<ChartLoader />}>
                <Chart
                  options={barChartData.options}
                  series={barChartData.series}
                  type="bar"
                  height={300}
                />
              </Suspense>
            )}
          </Card>
        </Col>

        {/* Questions by Type - Donut Chart */}
        <Col xs={24} lg={10}>
          <Card title={t("pages.dashboard.questionsByType")} loading={isLoading}>
            {!isLoading && donutChartData.series.length > 0 ? (
              <Suspense fallback={<ChartLoader />}>
                <Chart
                  options={donutChartData.options}
                  series={donutChartData.series}
                  type="donut"
                  height={300}
                />
              </Suspense>
            ) : !isLoading ? (
              <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography.Text type="secondary">{t("pages.dashboard.noData")}</Typography.Text>
              </div>
            ) : null}
          </Card>
        </Col>
      </Row>

      {/* Questions by Category - Horizontal Bar */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24}>
          <Card title={t("pages.dashboard.questionsByCategory")} loading={isLoading}>
            {!isLoading && horizontalBarData.series[0].data.length > 0 ? (
              <Suspense fallback={<ChartLoader />}>
                <Chart
                  options={horizontalBarData.options}
                  series={horizontalBarData.series}
                  type="bar"
                  height={300}
                />
              </Suspense>
            ) : !isLoading ? (
              <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography.Text type="secondary">{t("pages.dashboard.noData")}</Typography.Text>
              </div>
            ) : null}
          </Card>
        </Col>
      </Row>
    </motion.div>
  );
};
